import { Button, type ButtonVariations, Heading } from "@oysterhr/ui"
import classNames from "classnames"
import { noop } from "lodash-es"
import { CountryTag } from "../CountryTag"
import { StatusTag } from "../StatusTag"
import { gql } from "@apollo/client"
import type { EmploymentTemplate } from "@oysterhr/schema"
import { OysterTemplateDescription } from "./OysterTemplateDescription"
import { getCountryIsoCode } from "@oysterhr/common"

export type ButtonDefinition = {
  text?: string
  variation?: ButtonVariations
  onClick?: (templateId: string) => void
}

export type TemplateButtons = {
  primary?: ButtonDefinition
  secondary?: ButtonDefinition
}

type HiringTemplateCardProps = {
  employmentTemplate: HiringTemplateCardFragment
  actions: TemplateButtons
}

export const HiringTemplateCard = ({ employmentTemplate, actions }: HiringTemplateCardProps) => {
  const { id, name, description, countryCode, oysterScore, status } = employmentTemplate
  const tagClassName = "rounded-md p-2 text-xs"
  const validCountryCode = getCountryIsoCode(countryCode)

  return (
    <div
      className="flex flex-col justify-between rounded-lg border-2 border-gray-100 p-6 shadow-sm"
      data-testid={`hiring-template-card-${id || oysterScore}`}
    >
      <div>
        <Heading as="h5" className="break-words">
          {name}
        </Heading>
        {/* Divider */}
        <div className="my-4 border border-gray-100" />
        {/* Oyster Templates always have oysterScore and countryCode */}
        {oysterScore && validCountryCode ? (
          <OysterTemplateDescription oysterScore={oysterScore} countryCode={validCountryCode} />
        ) : (
          <>
            <div
              className="flex justify-between lg:flex-row"
              data-testid="hiring-template-card-scope-status"
            >
              <div className={classNames(tagClassName, "bg-gray-100 text-gray-400")}>
                <CountryTag countryCode={countryCode} size="xs" />
              </div>
              <StatusTag status={status} />
            </div>
            <div className="mb-8 mt-4 break-words text-sm text-gray-400 lg:mb-12">
              {description}
            </div>
          </>
        )}
      </div>
      <TemplateCardActions actions={actions} />
    </div>
  )
}

export const TemplateCardActions = ({ actions }: { actions: TemplateButtons }) => {
  return (
    <div className="flex flex-col justify-end space-y-2 xl:flex-row xl:space-x-2 xl:space-y-0">
      {actions.secondary && (
        <Button
          onClick={actions.secondary.onClick || noop}
          className="w-full xl:max-w-fit"
          variation={actions.secondary?.variation}
          data-testid="hiring-template-card-secondary-button"
        >
          {actions.secondary?.text}
        </Button>
      )}
      {actions.primary && (
        <Button
          onClick={actions.primary.onClick || noop}
          className="w-full xl:max-w-fit"
          variation={actions.primary?.variation}
          data-testid="hiring-template-card-primary-button"
        >
          {actions.primary?.text}
        </Button>
      )}
    </div>
  )
}

HiringTemplateCard.fragment = gql`
  fragment HiringTemplateCard on EmploymentTemplate {
    id
    name
    description
    countryCode
    oysterScore
    status
    company {
      id
      name
    }
  }
`

// @todo/codegen: Remove this type when we can use the generated type from the fragment
export type HiringTemplateCardFragment = Pick<
  EmploymentTemplate,
  "id" | "name" | "description" | "countryCode" | "oysterScore" | "status"
> & {
  company: Pick<EmploymentTemplate["company"], "id" | "name">
}
